import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockIcon from '@mui/icons-material/Lock';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  IconButton,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { Technician } from '../../../types/technician-types';
import { ProductType, TravelerTruss, getTrussDescription } from '../types';
import ProductionTracker from './production-tracker';
import TrussSpecifications from './truss-specifications';

interface TrussAccordionSectionProps {
  title: string;
  productType: ProductType;
  trusses: TravelerTruss[];
  technicians: Technician[];
  canRecordStages: boolean;
  canManageTrusses: boolean;
  canAdminOverride: boolean;
  onSaveStage: (
    trussId: number,
    stageId: number,
    data: { technicianId: string; date: string; initials: string }
  ) => Promise<void>;
  onAddTruss: (productType: ProductType) => void;
  onDeleteTruss: (trussId: number) => void;
  onFinalQcComplete: (truss: TravelerTruss) => void;
  onDirtyChange?: (isDirty: boolean) => void;
}

const TrussAccordionSection = ({
  title,
  productType,
  trusses,
  technicians,
  canRecordStages,
  canManageTrusses,
  canAdminOverride,
  onSaveStage,
  onAddTruss,
  onDeleteTruss,
  onFinalQcComplete,
  onDirtyChange,
}: TrussAccordionSectionProps) => {
  const [expandedTruss, setExpandedTruss] = useState<number | false>(
    trusses.find(t => !t.isLocked)?.id ?? false
  );

  const handleSaveStage = async (
    truss: TravelerTruss,
    stageId: number,
    data: { technicianId: string; date: string; initials: string }
  ) => {
    await onSaveStage(truss.id, stageId, data);

    const stage = truss.productionStages.find(s => s.id === stageId);
    if (stage?.isFinalQc) {
      onFinalQcComplete(truss);
    }
  };

  if (trusses.length === 0) {
    return (
      <Box
        sx={{
          bgcolor: '#fff',
          borderRadius: '8px',
          mb: 2,
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: '16px 20px',
          }}
        >
          <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#333' }}>{title}</Typography>
          {canManageTrusses && (
            <IconButton size="small" color="primary" onClick={() => onAddTruss(productType)}>
              <AddIcon />
            </IconButton>
          )}
        </Box>
        <Box
          sx={{
            textAlign: 'center',
            py: 3,
            color: '#888',
            fontSize: '13px',
            borderTop: '1px solid #f0f0f0',
          }}
        >
          No {title.toLowerCase()} for this building.
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: '#fff',
        borderRadius: '8px',
        mb: 2,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: '16px 20px',
        }}
      >
        <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#333' }}>{title}</Typography>
        {canManageTrusses && (
          <IconButton size="small" color="primary" onClick={() => onAddTruss(productType)}>
            <AddIcon />
          </IconButton>
        )}
      </Box>

      {trusses.map(truss => (
        <Accordion
          key={truss.id}
          expanded={expandedTruss === truss.id}
          onChange={(_, isExpanded) => setExpandedTruss(isExpanded ? truss.id : false)}
          disableGutters
          elevation={0}
          sx={{
            '&:before': { display: 'none' },
            borderTop: '1px solid #f0f0f0',
            bgcolor: truss.isLocked ? '#f0fff4' : '#fafafa',
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              px: 2.5,
              py: 0.5,
              bgcolor: truss.isLocked ? '#f0fff4' : '#fafafa',
              '&:hover': {
                bgcolor: truss.isLocked ? '#e8f5e9' : '#f5f5f5',
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                mr: 1,
              }}
            >
              <Typography sx={{ fontSize: '14px', color: '#333' }}>
                {getTrussDescription(truss)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Chip
                  label={`QTY: ${truss.quantity}`}
                  size="small"
                  sx={{
                    bgcolor: truss.isLocked ? '#d4edda' : '#e8f4fc',
                    color: truss.isLocked ? '#27ae60' : '#2980b9',
                    fontWeight: 600,
                    fontSize: '12px',
                  }}
                />
                {truss.isLocked && (
                  <Chip
                    icon={<LockIcon sx={{ fontSize: 12 }} />}
                    label="LOCKED"
                    size="small"
                    sx={{
                      bgcolor: '#27ae60',
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: '10px',
                      '& .MuiChip-icon': { color: '#fff' },
                    }}
                  />
                )}
                {truss.isLocked ? (
                  <CheckCircleIcon sx={{ color: '#27ae60', fontSize: 18 }} />
                ) : (
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      bgcolor: truss.productionStages.some(s => s.status === 'complete')
                        ? '#f39c12'
                        : '#bdc3c7',
                    }}
                  />
                )}
                {!truss.isLocked && canManageTrusses && (
                  <IconButton
                    size="small"
                    onClick={e => {
                      e.stopPropagation();
                      onDeleteTruss(truss.id);
                    }}
                    sx={{ color: '#e74c3c' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              p: 2.5,
              bgcolor: truss.isLocked ? '#f8fff8' : '#fff',
              borderTop: '1px solid #f0f0f0',
            }}
          >
            {/* Locked notice banner */}
            {truss.isLocked && (
              <Box
                sx={{
                  bgcolor: canAdminOverride ? '#fff3cd' : '#d4edda',
                  border: `1px solid ${canAdminOverride ? '#ffc107' : '#27ae60'}`,
                  borderRadius: '6px',
                  p: '12px 16px',
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  fontSize: '13px',
                  color: canAdminOverride ? '#856404' : '#155724',
                }}
              >
                <LockIcon sx={{ fontSize: 18 }} />
                <Typography
                  sx={{ fontSize: '13px', color: canAdminOverride ? '#856404' : '#155724' }}
                >
                  {canAdminOverride ? (
                    <>
                      <strong>Admin Override Active.</strong> This truss is complete but you may
                      edit production stages as an administrator.
                    </>
                  ) : (
                    <>
                      <strong>This truss is complete and locked.</strong> All production stages have
                      been verified by QC. No further edits allowed.
                    </>
                  )}
                </Typography>
              </Box>
            )}

            {/* Two-column layout — only activates at xl (≥1536px) to avoid narrow
                columns on tablets, including iPad Pro 12.9" landscape (1366px). */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' },
                gap: 3,
              }}
            >
              <TrussSpecifications
                truss={truss}
                specs={truss.specifications}
                isLocked={truss.isLocked}
              />
              <ProductionTracker
                stages={truss.productionStages}
                technicians={technicians}
                isLocked={truss.isLocked}
                canRecordStages={canRecordStages}
                canAdminOverride={canAdminOverride}
                onSaveStage={(stageId, data) => handleSaveStage(truss, stageId, data)}
                onDirtyChange={onDirtyChange}
              />
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default TrussAccordionSection;
